import { Component } from 'react'
import styled from '@emotion/styled'
import { format } from 'date-fns'
import { Box, Text, Button, KeyValueRow, Page, PrivateImage, Spacer, Icons } from 'stylewhere/components'
import { AppStore, RNEventSource, Router } from 'stylewhere/shared'
import { __ } from 'stylewhere/i18n'
import { Items, TmrItem, TmrPlace } from 'stylewhere/api'
import { DeskNotice as DeskNoticeApi, DeskNoticeEvent, DeskNoticeDecodeResult } from 'custom/cucinelli/api/DeskNotice'
import { canViewDeskNotice } from 'custom/cucinelli/shared/utils'

const MAX_ITEMS = 200

type SseStatus = 'idle' | 'connecting' | 'reconnecting' | 'open' | 'error'

interface NotifiedItem {
  key: string
  epc: string
  item?: TmrItem | null
  decodeFailed?: boolean
  sourceCode?: string
  operationDate?: string
  receivedAt: number
}

interface State {
  places: TmrPlace[]
  selectedPlace?: TmrPlace
  sseStatus: SseStatus
  items: NotifiedItem[]
}

export default class DeskNotice extends Component<{}, State> {
  state: State = {
    places: [],
    selectedPlace: undefined,
    sseStatus: 'idle',
    items: []
  }

  private rowSeq = 0

  componentDidMount() {
    if (!canViewDeskNotice()) {
      Router.navigate('/')
      return
    }
    const places = AppStore.loggedUser?.userPlaces ?? []
    this.setState({ places }, () => {
      if (places.length === 1) {
        this.selectPlace(places[0])
      }
    })
  }

  componentWillUnmount() {
    RNEventSource.destroy(true)
  }

  selectPlace = (place: TmrPlace) => {
    if (place.id === this.state.selectedPlace?.id) return
    this.setState({ selectedPlace: place, items: [] }, this.attachSse)
  }

  attachSse = () => {
    const { selectedPlace } = this.state
    if (!selectedPlace) return

    const sseUrl = DeskNoticeApi.getSseEndpoint(selectedPlace.id)
    RNEventSource.destroy(true)
    this.setState({ sseStatus: 'connecting' })
    RNEventSource.addEventListener('open', this.onSseOpen)
    RNEventSource.addEventListener('message', (e) => this.onSseMessage(e))
    RNEventSource.addEventListener('error', this.onSseError)
    RNEventSource.addEventListener('keepAlive', this.onSseKeepAlive)
    RNEventSource.initialize(sseUrl)
  }

  onSseOpen = () => this.setState({ sseStatus: 'open' })

  onSseError = () => this.setState({ sseStatus: 'error' })

  onSseKeepAlive = () => this.setState({ sseStatus: 'reconnecting' })

  onSseMessage = async (event: { data?: string }) => {
    const placeId = this.state.selectedPlace?.id
    const payload = this.parseEvent(event)
    const epcs = (payload?.tagReads ?? []).map((t) => t.identifierCode).filter(Boolean)
    if (epcs.length === 0) return

    let decoded: DeskNoticeDecodeResult = {}
    let decodeFailed = false
    try {
      decoded = (await Items.batchDecode<DeskNoticeDecodeResult>(epcs, [])) ?? {}
    } catch {
      decodeFailed = true
    }

    if (this.state.selectedPlace?.id !== placeId) return

    const receivedAt = Date.now()
    const incoming: NotifiedItem[] = epcs.map((epc) => ({
      key: `${this.rowSeq++}`,
      epc,
      item: decoded[epc] ?? null,
      decodeFailed,
      sourceCode: payload?.sourceCode,
      operationDate: payload?.operationdate,
      receivedAt
    }))

    this.setState((prev) => ({ items: [...incoming, ...prev.items].slice(0, MAX_ITEMS) }))
  }

  parseEvent = (event: { data?: string }): DeskNoticeEvent | undefined => {
    try {
      let data: any = event?.data
      if (typeof data === 'string') data = JSON.parse(data)
      if (data && !data.tagReads && data.data) data = data.data
      return data as DeskNoticeEvent
    } catch {
      return undefined
    }
  }

  getPlaceLabel = (place?: TmrPlace) => place?.description || place?.code || '---'

  getStatusLabel = () => {
    switch (this.state.sseStatus) {
      case 'open':
        return __('desk_notice.status_listening')
      case 'connecting':
        return __('desk_notice.status_connecting')
      case 'reconnecting':
        return __('desk_notice.status_reconnecting')
      case 'error':
        return __('desk_notice.status_error')
      default:
        return __('desk_notice.status_idle')
    }
  }

  renderRow = (notified: NotifiedItem) => {
    const product = notified.item?.product
    const title = notified.decodeFailed
      ? __('desk_notice.decode_error_item')
      : product?.description || __('desk_notice.unknown_item')
    return (
      <Row key={notified.key}>
        <PrivateImage product={product} width={64} height={64} variant="boxed" />
        <Box flex ml={15}>
          <Text bold fontSize={18}>
            {title}
          </Text>
          <Text fontSize={14}>{product?.code || '---'}</Text>
          <Text fontSize={13}>{`EPC: ${notified.epc}`}</Text>
        </Box>
        <Box style={{ textAlign: 'right' }}>
          {notified.sourceCode && <Text fontSize={13}>{`${__('desk_notice.source')}: ${notified.sourceCode}`}</Text>}
          <Text fontSize={13}>{format(new Date(notified.receivedAt), 'HH:mm:ss')}</Text>
        </Box>
      </Row>
    )
  }

  render() {
    const { places, selectedPlace, items, sseStatus } = this.state
    const placeOptions = places.map((p) => ({ ...p, label: this.getPlaceLabel(p) }))
    return (
      <Page title={__('desk_notice.title')}>
        <Page.Content notBoxed>
          {places.length === 0 ? (
            <Box flex center>
              <Text fontSize={20}>{__('desk_notice.no_places')}</Text>
            </Box>
          ) : (
            <Box flex>
              <KeyValueRow
                label={__('desk_notice.select_place')}
                value={selectedPlace ? this.getPlaceLabel(selectedPlace) : '---'}
                image={<Icons.Antenna />}
                optionModal={{
                  onSelect: (item) => this.selectPlace(item[0]),
                  field: 'label',
                  options: placeOptions,
                  selected: selectedPlace ? placeOptions.filter((p) => p.id === selectedPlace.id) : [],
                  title: __('desk_notice.select_place'),
                  searchable: true
                }}
              />
              <Spacer />
              <StatusBadge>
                <StatusDot status={sseStatus} />
                <StatusLabel status={sseStatus}>{this.getStatusLabel()}</StatusLabel>
                {sseStatus === 'error' && (
                  <Button variant="secondary" size="small" type="button" onClick={this.attachSse}>
                    {__('desk_notice.reconnect')}
                  </Button>
                )}
              </StatusBadge>
              <Spacer />
              {selectedPlace && items.length === 0 && (
                <Box flex center>
                  <Text fontSize={18}>{__('desk_notice.waiting')}</Text>
                </Box>
              )}
              <List>{items.map(this.renderRow)}</List>
            </Box>
          )}
        </Page.Content>
      </Page>
    )
  }
}

const List = styled(Box)`
  overflow-y: auto;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 1px 5px 0 #00000014;
  padding: 12px 16px;
  margin-bottom: 12px;
`

const STATUS_COLOR: Record<SseStatus, string> = {
  open: '#3cbf6f',
  error: '#f43c3c',
  reconnecting: '#e6a23c',
  connecting: '#999999',
  idle: '#999999'
}

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 2px;
`

const StatusDot = styled.span<{ status: SseStatus }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ status }) => STATUS_COLOR[status]};
`

const StatusLabel = styled.span<{ status: SseStatus }>`
  font-size: 18px;
  font-weight: 600;
  color: ${({ status }) => STATUS_COLOR[status]};
`
